import type { SvgJob } from "../../lib/types";
import { getProgress, isTerminal } from "./status";

export type PhaseState = "pending" | "active" | "complete" | "error";
export type JobPhase = {
  key: string;
  label: string;
  state: PhaseState;
  percent: number;
  at: string | null;
  elapsedMilliseconds: number | null;
};

function elapsed(start: string, end: string | null, now: number): number | null {
  const startAt = Date.parse(start);
  const endAt = end ? Date.parse(end) : now;
  if (!Number.isFinite(startAt) || !Number.isFinite(endAt) || endAt < startAt) return null;
  return endAt - startAt;
}

export function getJobPhases(job: SvgJob, now = Date.now()): JobPhase[] {
  const progress = getProgress(job);
  const failed = job.status === "failed";
  const cancelled = job.status === "cancelled";
  const expired = job.status === "expired";
  const end = isTerminal(job) ? job.completed_at || job.updated_at : null;
  const definitions = [
    { key: "source", label: "源文件", threshold: 20, at: job.uploaded_at },
    { key: "queue", label: "排队", threshold: 35, at: job.started_at || job.queued_at },
    { key: "render", label: "绘制", threshold: 82, at: job.rendering_at || job.started_at },
    { key: "save", label: "保存", threshold: 96, at: job.uploading_at },
    { key: "finish", label: failed ? "失败" : cancelled ? "取消" : expired ? "过期" : "完成", threshold: 100, at: end },
  ];

  return definitions.map((phase, index) => {
    const previousThreshold = index === 0 ? 0 : definitions[index - 1]!.threshold;
    let state: PhaseState = "pending";
    if (progress >= phase.threshold || (index === definitions.length - 1 && isTerminal(job))) state = "complete";
    else if (progress >= previousThreshold) state = "active";

    if ((failed || cancelled) && index === definitions.findIndex((candidate) => progress < candidate.threshold)) state = "error";
    if ((failed || cancelled) && index === definitions.length - 1) state = "error";

    return {
      key: phase.key,
      label: phase.label,
      state,
      percent: phase.threshold,
      at: phase.at,
      elapsedMilliseconds: phase.at ? elapsed(job.created_at, phase.at, now) : state === "active" ? elapsed(job.created_at, null, now) : null,
    };
  });
}

export function getTimingSummary(job: SvgJob, now = Date.now()): { label: string; milliseconds: number | null } {
  const end = isTerminal(job) ? job.completed_at || job.updated_at : null;
  return {
    label: isTerminal(job) ? "总耗时" : "已运行",
    milliseconds: elapsed(job.created_at, end, now),
  };
}
